// frontend/src/features/ups/modals/DriverDetailsModal.js
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  uploadDriverPhoto as svcUpload,
  deleteDriverPhoto as svcDelete,
} from '../../../services/driverService';
import {
  upsertDriver,
  selectDriverById,
} from '../../drivers/driverSlice';

import MoveSiteModal from './MoveSiteModal';
import BannedDriverEditModal from '../BannedDriverEditModal';
import { COMPANY_ACTION_CLASSES } from '../ui';

const buildDetailsDraft = (data) => ({
  name: data.name || '',
  vehicle_registration_number:
    data.vehicle_registration_number || data.vrn || '',
  phone: data.phone || '',
  email: data.email || '',
});

export default function DriverDetailsModal({
  driver,
  onClose,
  onEditDriver,
  onSaveDetails,
  onMoveSuccess,
  refreshAssignmentsAfterMove = true,
  allowMoveSite = true,
}) {
  const closeBtnRef = useRef(null);
  const draftDriverIdRef = useRef(null);
  const dispatch = useDispatch();

  const [isBusy, setIsBusy] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [detailsDraft, setDetailsDraft] = useState({
    name: '',
    vehicle_registration_number: '',
    phone: '',
    email: '',
  });
  const [detailsErrors, setDetailsErrors] = useState({});
  const [isSavingDetails, setIsSavingDetails] = useState(false);
  const [detailsSaveError, setDetailsSaveError] = useState('');

  // Controls nested modals opened from this drawer
  const [showMoveSiteModal, setShowMoveSiteModal] = useState(false);
  const [showBanEditModal, setShowBanEditModal] = useState(false);

  // Pull latest driver data from Redux when available
  const liveDriver = useSelector((state) =>
    driver?.id != null ? selectDriverById(state, driver.id) : null
  );

  // Focus close button when drawer opens
  useEffect(() => {
    closeBtnRef.current?.focus();
  }, []);

  // Lock page scroll while drawer is open
  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  // Close drawer with Escape unless a nested modal is open
  useEffect(() => {
    const onKey = (e) => {
      if (
        e.key === 'Escape' &&
        !showMoveSiteModal &&
        !showBanEditModal &&
        !isSavingDetails
      ) {
        onClose?.();
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, showMoveSiteModal, showBanEditModal, isSavingDetails]);

  // Display helpers
  const safe = (v) => v ?? '—';
  const safeDate = (v) => (v ? new Date(v).toLocaleString() : '—');

  // Preserve original row context while allowing Redux data to override it
  const rowContext = driver || {};
  const driverCore = liveDriver || {};

  // Driver identity and profile data
  const driverData = {
    id: driverCore.id ?? rowContext.id,
    name: driverCore.name ?? rowContext.name,
    vehicle_registration_number:
      driverCore.vehicle_registration_number ?? rowContext.vehicle_registration_number,
    vrn: driverCore.vrn ?? rowContext.vrn,
    phone: driverCore.phone ?? rowContext.phone,
    email: driverCore.email ?? rowContext.email,
    agency_name: driverCore.agency_name ?? rowContext.agency_name,
    agency: driverCore.agency ?? rowContext.agency,
    company_id: driverCore.company_id ?? rowContext.company_id,
    workforce_source: driverCore.workforce_source ?? rowContext.workforce_source,
    submission_date: driverCore.submission_date ?? rowContext.submission_date,
    photo_url: driverCore.photo_url ?? rowContext.photo_url,
    photo_mime: driverCore.photo_mime ?? rowContext.photo_mime,
    photo_updated_at: driverCore.photo_updated_at ?? rowContext.photo_updated_at,
  };

  // Assignment, ban, and operational context
  const assignmentContext = {
    assignment_id: rowContext.assignment_id ?? null,
    assignment_status: rowContext.assignment_status ?? null,
    starts_at: rowContext.starts_at ?? rowContext.start_time ?? null,
    ends_at: rowContext.ends_at ?? rowContext.end_time ?? null,
    site_id: rowContext.site_id ?? null,
    site_name: rowContext.site_name ?? null,
    current_state: rowContext.current_state ?? null,
    is_banned: rowContext.is_banned ?? null,
    ban_id: rowContext.ban_id ?? null,
    ban_reason: rowContext.ban_reason ?? null,
    banned_at: rowContext.banned_at ?? null,
    blocked_reason: rowContext.blocked_reason ?? rowContext.reason ?? null,
  };

  const { id, name, photo_url, photo_updated_at } = driverData;
  const canInlineEditDetails = typeof onSaveDetails === 'function';
  const draftName = driverData.name || '';
  const draftVehicle =
    driverData.vehicle_registration_number || driverData.vrn || '';
  const draftPhone = driverData.phone || '';
  const draftEmail = driverData.email || '';

  useEffect(() => {
    const driverChanged = draftDriverIdRef.current !== driverData.id;

    if (driverChanged || !isEditingDetails) {
      setDetailsDraft({
        name: draftName,
        vehicle_registration_number: draftVehicle,
        phone: draftPhone,
        email: draftEmail,
      });
      setDetailsErrors({});
      setDetailsSaveError('');
      draftDriverIdRef.current = driverData.id;

      if (driverChanged && isEditingDetails) {
        setIsEditingDetails(false);
      }
    }
  }, [
    driverData.id,
    draftName,
    draftVehicle,
    draftPhone,
    draftEmail,
    isEditingDetails,
  ]);

  // Ownership label shown in the core fields section
  const ownershipLabel =
    driverData.workforce_source === 'COMPANY' || driverData.company_id
      ? 'Internal / Company-owned'
      : driverData.agency_name || driverData.agency?.name
      ? `Agency — ${driverData.agency_name || driverData.agency?.name}`
      : 'Agency';

  // Move Site is only available for open movable assignments
  const hasMovableAssignment =
    !!assignmentContext.assignment_id &&
    ['requested', 'confirmed', 'active'].includes(
      String(assignmentContext.assignment_status || '').toLowerCase()
    );

  const canEditBan = !!(assignmentContext.banned_at || assignmentContext.ban_reason);
  const canMoveSite = allowMoveSite && hasMovableAssignment;

  // Show operations area when any advanced action is relevant
  const showAdvancedOperations = canMoveSite || canEditBan || allowMoveSite;

  const operationalStatusLabel =
    assignmentContext.current_state || assignmentContext.assignment_status || '—';

  if (!id) return null;

  function updateDetailsDraft(field, value) {
    setDetailsDraft((prev) => ({
      ...prev,
      [field]:
        field === 'vehicle_registration_number' ? value.toUpperCase() : value,
    }));

    setDetailsErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
    setDetailsSaveError('');
  }

  function validateDetailsDraft() {
    const nextErrors = {};
    const email = detailsDraft.email.trim();
    const phone = detailsDraft.phone.trim();

    if (!detailsDraft.name.trim()) {
      nextErrors.name = 'Name is required.';
    }

    if (!detailsDraft.vehicle_registration_number.trim()) {
      nextErrors.vehicle_registration_number = 'Vehicle is required.';
    }

    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      nextErrors.email = 'Enter a valid email address.';
    }

    if (phone.length > 50) {
      nextErrors.phone = 'Phone must be 50 characters or fewer.';
    }

    return nextErrors;
  }

  function handleStartDetailsEdit() {
    setDetailsDraft(buildDetailsDraft(driverData));
    setDetailsErrors({});
    setDetailsSaveError('');
    setIsEditingDetails(true);
  }

  function handleCancelDetailsEdit() {
    if (isSavingDetails) return;
    setDetailsDraft(buildDetailsDraft(driverData));
    setDetailsErrors({});
    setDetailsSaveError('');
    setIsEditingDetails(false);
  }

  async function handleSaveDetails() {
    if (!canInlineEditDetails || isSavingDetails) return;

    const nextErrors = validateDetailsDraft();
    if (Object.keys(nextErrors).length > 0) {
      setDetailsErrors(nextErrors);
      setDetailsSaveError('');
      return;
    }

    const payload = {
      name: detailsDraft.name.trim(),
      vehicle_registration_number:
        detailsDraft.vehicle_registration_number.trim(),
      phone: detailsDraft.phone.trim() || null,
      email: detailsDraft.email.trim() || null,
    };

    try {
      setIsSavingDetails(true);
      setDetailsErrors({});
      setDetailsSaveError('');
      await onSaveDetails(driverData.id, payload, driverData);
      setDetailsErrors({});
      setIsEditingDetails(false);
    } catch (err) {
      setDetailsSaveError(
        err?.response?.data?.error ||
          err?.message ||
          'Could not save driver details.'
      );
    } finally {
      setIsSavingDetails(false);
    }
  }

  const detailsInputClass =
    'mt-1 w-full rounded-xl border border-app-border bg-app-surface px-3 py-1.5 text-sm font-semibold text-app-text outline-none transition placeholder:text-app-muted focus:border-app-primary focus:ring-2 focus:ring-app-primary/15 disabled:cursor-not-allowed disabled:opacity-60';
  const detailsErrorClass = 'mt-2 text-xs font-semibold text-red-700';
  const detailsActionsDisabled = isSavingDetails;

  // Upload driver photo and update Redux cache
  async function handleUploadPhoto(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setIsBusy(true);
      setErrorMsg('');

      const updated = await svcUpload(id, file);
      if (updated) dispatch(upsertDriver(updated));
    } catch (err) {
      setErrorMsg('Could not upload photo.');
    } finally {
      setIsBusy(false);
    }
  }

  // Remove driver photo and update Redux cache
  async function handleRemovePhoto() {
    try {
      setIsBusy(true);
      setErrorMsg('');

      const updated = await svcDelete(id);
      if (updated) dispatch(upsertDriver(updated));
    } catch (err) {
      setErrorMsg('Could not remove photo.');
    } finally {
      setIsBusy(false);
    }
  }

  // Cache-bust photo when updated
  const ver = photo_updated_at ? new Date(photo_updated_at).getTime() : Date.now();
  const src = photo_url ? `${photo_url}?v=${ver}` : '/img/placeholder-avatar.png';
  const alertClass =
    'rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-medium text-red-700';
  const warningSurfaceClass =
    'rounded-xl border border-amber-200 bg-amber-50 p-3 text-amber-800';

  return (
    <>
      {/* Overlay */}
      <div
        className="absolute inset-x-0 bottom-0 top-[var(--ups-shell-header-h)] z-40"
        onClick={() => {
          if (!showMoveSiteModal && !showBanEditModal && !isSavingDetails) {
            onClose?.();
          }
        }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 bg-black/20" />

        {/* Responsive drawer/modal shell */}
        <aside
          className="absolute right-0 top-0 flex h-full w-full flex-col overflow-hidden border-l border-app-border bg-app-card text-app-text shadow-xl sm:w-[520px] md:w-[640px]"
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
        >
          {/* Fixed header */}
          <div className="flex shrink-0 items-start justify-between gap-4 border-b border-app-border px-4 py-4 sm:px-6 sm:py-5">
            <div className="min-w-0">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-app-muted">
                Selected Driver
              </p>
              <h2 className="mt-1 break-words text-lg font-bold text-app-text sm:text-xl">
                {safe(name)}
              </h2>
            </div>

            <button
              onClick={onClose}
              disabled={isSavingDetails}
              ref={closeBtnRef}
              className="rounded-xl p-2 text-app-muted transition hover:bg-app-surface hover:text-app-text disabled:cursor-not-allowed disabled:opacity-60"
              aria-label="Close driver details"
            >
              &times;
            </button>
          </div>

          {/* Scrollable details body */}
          <div className="min-h-0 flex-1 overflow-y-auto px-4 py-4 sm:px-6 sm:py-5">
            {/* Error message */}
            {errorMsg && (
              <div className={`mb-4 ${alertClass}`}>
                {errorMsg}
              </div>
            )}

            {detailsSaveError && (
              <div className={`mb-4 ${alertClass}`}>
                {detailsSaveError}
              </div>
            )}

            {/* Photo section */}
            <div className="mb-5 rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5">
              <p className="mb-3 text-sm font-bold text-app-text">Photo</p>

              <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
                <img
                  src={src}
                  alt="Driver"
                  className="h-24 w-24 shrink-0 rounded-2xl border border-app-border bg-app-card object-cover"
                />

                <div className="flex min-w-0 flex-wrap gap-2 sm:flex-col sm:items-start">
                  <label
                    className={`cursor-pointer ${
                      isBusy
                        ? 'inline-flex items-center justify-center rounded-xl border border-app-border bg-app-surface px-5 py-2.5 text-sm font-semibold text-app-muted'
                        : COMPANY_ACTION_CLASSES.primary
                    }`}
                  >
                    {isBusy ? 'Working...' : 'Upload photo'}
                    <input
                      type="file"
                      className="hidden"
                      onChange={handleUploadPhoto}
                      disabled={isBusy}
                    />
                  </label>

                  {photo_url && (
                    <button
                      onClick={handleRemovePhoto}
                      disabled={isBusy}
                      className={COMPANY_ACTION_CLASSES.compactDestructive}
                    >
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>

            {/* Core driver fields */}
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1">
                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Name
                  </p>
                  {isEditingDetails ? (
                    <>
                      <input
                        type="text"
                        value={detailsDraft.name}
                        onChange={(e) => updateDetailsDraft('name', e.target.value)}
                        disabled={isSavingDetails}
                        className={detailsInputClass}
                        aria-label="Driver name"
                      />
                      {detailsErrors.name && (
                        <p className={detailsErrorClass}>
                          {detailsErrors.name}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 break-words font-bold text-app-text">
                      {safe(driverData.name)}
                    </p>
                  )}
                </div>

                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Vehicle
                  </p>
                  {isEditingDetails ? (
                    <>
                      <input
                        type="text"
                        value={detailsDraft.vehicle_registration_number}
                        onChange={(e) =>
                          updateDetailsDraft(
                            'vehicle_registration_number',
                            e.target.value
                          )
                        }
                        disabled={isSavingDetails}
                        className={detailsInputClass}
                        aria-label="Vehicle registration number"
                      />
                      {detailsErrors.vehicle_registration_number && (
                        <p className={detailsErrorClass}>
                          {detailsErrors.vehicle_registration_number}
                        </p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 break-words font-bold text-app-text">
                      {safe(driverData.vehicle_registration_number || driverData.vrn)}
                    </p>
                  )}
                </div>

                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Phone
                  </p>
                  {isEditingDetails ? (
                    <>
                      <input
                        type="tel"
                        value={detailsDraft.phone}
                        onChange={(e) => updateDetailsDraft('phone', e.target.value)}
                        disabled={isSavingDetails}
                        className={detailsInputClass}
                        aria-label="Driver phone"
                      />
                      {detailsErrors.phone && (
                        <p className={detailsErrorClass}>{detailsErrors.phone}</p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 break-words font-bold text-app-text">
                      {safe(driverData.phone)}
                    </p>
                  )}
                </div>

                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4 sm:col-span-2 lg:col-span-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Email
                  </p>
                  {isEditingDetails ? (
                    <>
                      <input
                        type="email"
                        value={detailsDraft.email}
                        onChange={(e) => updateDetailsDraft('email', e.target.value)}
                        disabled={isSavingDetails}
                        className={detailsInputClass}
                        aria-label="Driver email"
                      />
                      {detailsErrors.email && (
                        <p className={detailsErrorClass}>{detailsErrors.email}</p>
                      )}
                    </>
                  ) : (
                    <p className="mt-1 break-all font-bold text-app-text">
                      {safe(driverData.email)}
                    </p>
                  )}
                </div>

                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4 sm:col-span-2 lg:col-span-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Ownership
                  </p>
                  <p className="mt-1 break-words font-bold text-app-text">
                    {ownershipLabel}
                  </p>
                </div>

                <div className="min-w-0 rounded-2xl border border-app-border bg-app-card p-4 sm:col-span-2 lg:col-span-1">
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-app-muted">
                    Submitted
                  </p>
                  <p className="mt-1 break-words font-bold text-app-text">
                    {safeDate(driverData.submission_date)}
                  </p>
                </div>
              </div>

              {/* Ban information */}
              {(assignmentContext.banned_at || assignmentContext.ban_reason) && (
                <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-red-800 sm:p-5">
                  <p className="mb-2 font-bold text-red-800">Ban Info</p>
                  <p className="break-words">
                    <strong>Banned At:</strong>{' '}
                    {safeDate(assignmentContext.banned_at)}
                  </p>
                  <p className="break-words">
                    <strong>Reason:</strong>{' '}
                    {safe(assignmentContext.ban_reason)}
                  </p>
                </div>
              )}

              {/* Operational assignment context */}
              <div className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5">
                <p className="mb-2 font-bold text-app-text">Operational Context</p>
                <p className="break-words">
                  <strong>Status:</strong>{' '}
                  <span className="capitalize">{operationalStatusLabel}</span>
                </p>
                <p className="break-words">
                  <strong>Current Site:</strong>{' '}
                  {safe(assignmentContext.site_name)}
                </p>

                {assignmentContext.blocked_reason && (
                  <p className={`mt-3 break-words ${warningSurfaceClass}`}>
                    <strong>Blocked Reason:</strong>{' '}
                    {assignmentContext.blocked_reason}
                  </p>
                )}
              </div>
            </div>

            {/* Advanced operations */}
            {showAdvancedOperations && (
              <div className="mt-6 border-t border-app-border pt-5">
                <p className="mb-3 font-bold text-app-text">
                  Advanced Operations
                </p>

                <div className="flex flex-wrap gap-2">
                  {allowMoveSite && (
                    <button
                      onClick={() => setShowMoveSiteModal(true)}
                      disabled={!canMoveSite || detailsActionsDisabled}
                      className={
                        canMoveSite && !detailsActionsDisabled
                          ? COMPANY_ACTION_CLASSES.primary
                          : 'inline-flex cursor-not-allowed items-center justify-center rounded-xl border border-app-border bg-app-surface px-5 py-2.5 text-sm font-semibold text-app-muted/50'
                      }
                    >
                      Move Site
                    </button>
                  )}

                  {canEditBan && (
                    <button
                      onClick={() => setShowBanEditModal(true)}
                      disabled={detailsActionsDisabled}
                      className={COMPANY_ACTION_CLASSES.primary}
                    >
                      Edit Ban
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Fixed footer */}
          <div
            className="flex shrink-0 flex-wrap justify-end gap-2 border-t border-app-border bg-app-surface px-4 py-3 sm:px-6 sm:py-4"
            style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
          >
            {isEditingDetails ? (
              <>
                <button
                  onClick={handleCancelDetailsEdit}
                  disabled={isSavingDetails}
                  className={COMPANY_ACTION_CLASSES.secondary}
                >
                  Cancel
                </button>

                <button
                  onClick={handleSaveDetails}
                  disabled={isSavingDetails}
                  className={COMPANY_ACTION_CLASSES.primary}
                >
                  {isSavingDetails ? 'Saving...' : 'Save changes'}
                </button>
              </>
            ) : (
              <>
                {(canInlineEditDetails || onEditDriver) && (
                  <button
                    onClick={() =>
                      canInlineEditDetails
                        ? handleStartDetailsEdit()
                        : onEditDriver(driverData)
                    }
                    disabled={isSavingDetails}
                    className={COMPANY_ACTION_CLASSES.primary}
                  >
                    Edit details
                  </button>
                )}

                <button
                  onClick={onClose}
                  disabled={isSavingDetails}
                  className={COMPANY_ACTION_CLASSES.secondary}
                >
                  Close
                </button>
              </>
            )}
          </div>
        </aside>
      </div>

      {/* Move Site nested modal */}
      {showMoveSiteModal && (
        <MoveSiteModal
          driverData={driverData}
          assignmentContext={assignmentContext}
          refreshAssignmentsAfterMove={refreshAssignmentsAfterMove}
          onClose={() => setShowMoveSiteModal(false)}
          onMoveSuccess={() => {
            setShowMoveSiteModal(false);
            onMoveSuccess?.();
            onClose?.();
          }}
        />
      )}

      {/* Edit Ban nested modal */}
      {showBanEditModal && (
        <BannedDriverEditModal
          ban={{
            ...driverData,
            ...assignmentContext,
            ban_id: assignmentContext.ban_id,
          }}
          onClose={() => setShowBanEditModal(false)}
          onUpdate={() => {
            setShowBanEditModal(false);
            onClose?.();
          }}
        />
      )}
    </>
  );
}
